import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button, ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type AppDialogAction = {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  // A confirm/cancel pair needs the two buttons to read differently; without
  // this every action renders as the primary one.
  variant?: ButtonProps["variant"];
};

export type AppDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;

  actions?: AppDialogAction[];
  showClose?: boolean;

  size?: "sm" | "md" | "lg";
  align?: "center" | "left";

  contentClassName?: string;
};

const SIZE_CLASS: Record<NonNullable<AppDialogProps["size"]>, string> = {
  sm: "sm:max-w-[360px]",
  md: "sm:max-w-[420px] min-h-[250px]",
  lg: "sm:max-w-[520px]",
};

export function AppDialog({
  open,
  onOpenChange,
  icon,
  title,
  description,
  actions = [],
  showClose = true,
  size = "md",
  align = "center",
  contentClassName,
}: AppDialogProps) {
  const isCenter = align === "center";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={showClose}
        className={cn(
          SIZE_CLASS[size],
          "rounded-lg p-6",
          isCenter ? "text-center" : "text-left",
          contentClassName
        )}
      >
        <DialogHeader className={cn(isCenter && "items-center", "gap-3")}>
          {icon && <div>{icon}</div>}
          {title && (
            <DialogTitle className="text-xl font-semibold mt-6 mb-0.5">
              {title}
            </DialogTitle>
          )}
          {description && (
            <DialogDescription
              className="text-sm text-muted-foreground text-center"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </DialogHeader>

        {actions.length > 0 && (
          <DialogFooter className="mt-6 flex flex-col gap-2 sm:flex-col">
            {actions.map((a, i) => (
              <Button
                key={i}
                className="w-full"
                disabled={a.disabled}
                onClick={a.onClick}
                variant={a.variant}
                type="button"
              >
                {a.label}
              </Button>
            ))}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
